import { Dialog } from '@angular/cdk/dialog';
import { NgClass } from '@angular/common';
import { Component, forwardRef, inject, input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GameManager, gameManager } from 'src/app/game/businesslogic/GameManager';
import { SettingsManager, settingsManager } from 'src/app/game/businesslogic/SettingsManager';
import { LootType } from 'src/app/game/model/data/Loot';
import { MonsterData } from 'src/app/game/model/data/MonsterData';
import { MonsterType } from 'src/app/game/model/data/MonsterType';
import { StatEffectRule } from 'src/app/game/model/data/ScenarioRule';
import { Monster } from 'src/app/game/model/Monster';
import { Scenario } from 'src/app/game/model/Scenario';
import { MonsterStatsComponent } from 'src/app/ui/figures/monster/stats/stats';
import { StatsListComponent } from 'src/app/ui/footer/scenario/dialog/stats-list/stats-list';
import { GhsLabelDirective } from 'src/app/ui/helper/label';
import { TabClickDirective } from 'src/app/ui/helper/tabclick';
import { TrackUUIDPipe } from 'src/app/ui/helper/trackUUID';

@Component({
  imports: [NgClass, FormsModule, GhsLabelDirective, TabClickDirective, TrackUUIDPipe, forwardRef(() => MonsterStatsComponent)],
  selector: 'ghs-scenario-setup',
  templateUrl: './scenario-setup.html',
  styleUrls: ['./scenario-setup.scss']
})
export class ScenarioSetupComponent implements OnInit {
  private dialog = inject(Dialog);

  readonly inputScenario = input.required<Scenario>({ alias: 'scenario' });
  get scenario(): Scenario {
    return this.inputScenario();
  }

  readonly inputSpoiler = input<boolean>(false, { alias: 'spoiler' });
  readonly title = input<boolean>(true);
  readonly details = input<boolean>(true);

  gameManager: GameManager = gameManager;
  settingsManager: SettingsManager = settingsManager;

  monsters: Monster[] = [];
  lootConfig: { type: LootType; value: number }[] = [];
  lootRandomItem: boolean = false;
  spoiler: boolean = false;
  hasSpoiler: boolean = false;
  detailed: boolean = false;

  ngOnInit(): void {
    this.spoiler = this.inputSpoiler();
    this.updateMonster();
    if (this.scenario.lootDeckConfig) {
      for (const value in LootType) {
        const lootType: LootType = value as LootType;
        if (this.scenario.lootDeckConfig[lootType]) {
          this.lootConfig.push({ type: lootType, value: this.scenario.lootDeckConfig[lootType] || 0 });
          if (
            lootType === LootType.random_item &&
            gameManager.game.party.randomItemLooted.find(
              (model) =>
                model.edition === this.scenario.edition && model.group === this.scenario.group && model.index === this.scenario.index
            )
          ) {
            this.lootRandomItem = true;
          }
        }
      }
    }
  }

  updateMonster() {
    this.monsters = [];
    const cs = gameManager.editionRules('cs');
    const sections = !gameManager.editionRules('fc') || this.spoiler;
    this.hasSpoiler = false;
    if (
      !sections &&
      gameManager.scenarioManager.getMonsters(this.scenario, false, this.scenario.custom).length !=
        gameManager.scenarioManager.getMonsters(this.scenario, true, this.scenario.custom).length
    ) {
      this.hasSpoiler = true;
    }

    gameManager.scenarioManager.getMonsters(this.scenario, sections, this.scenario.custom).forEach((monsterData) => {
      const monster: Monster = new Monster(monsterData, gameManager.game.level);
      if (
        this.spoiler ||
        !monster.standeeShare ||
        gameManager.scenarioManager
          .openRooms()
          .find((room) => room.initial && room.monster.find((standee) => standee.name.split(':')[0] === monster.name)) ||
        gameManager.game.figures.some(
          (figure) => figure instanceof Monster && figure.name === monster.name && figure.edition === monster.edition
        )
      ) {
        if (!this.monsters.find((m) => m.edition === monster.edition && m.name === monster.name)) {
          monster.tags = [];
          this.monsters.push(monster);
        }
      } else {
        const standee = gameManager
          .monstersData()
          .find(
            (monsterData) =>
              monsterData.name === monster.standeeShare && monsterData.edition === (monster.standeeShareEdition || monster.edition)
          );
        if (standee) {
          const changedStandee = new Monster(JSON.parse(JSON.stringify(standee)) as MonsterData);
          changedStandee.tags = changedStandee.tags || [];
          if (cs) {
            if (monster.boss) {
              changedStandee.tags.push('boss');
            }
          }

          const otherStandee = this.monsters.find((m) => m.name === changedStandee.name && m.edition === changedStandee.edition);
          if (!otherStandee) {
            this.hasSpoiler = true;
            this.monsters.push(changedStandee);
          } else if (!this.spoiler && cs) {
            otherStandee.tags = otherStandee.tags || [];
            if (monster.boss) {
              this.hasSpoiler = true;
              otherStandee.tags.push('boss');
            }
          }
        }
      }

      let statEffectRules = this.scenario.rules
        ? this.scenario.rules.filter((rule) => rule.statEffects).flatMap((rule) => rule.statEffects)
        : [];
      let statEffectRule: StatEffectRule | undefined = statEffectRules.find(
        (statEffectRule) => gameManager.figuresByIdentifier(statEffectRule.identifier, false, [monster]).length
      );

      if (!statEffectRule) {
        gameManager.scenarioManager.getSections(this.scenario).forEach((sectionData) => {
          if (!statEffectRule && sectionData.rules) {
            statEffectRules = sectionData.rules.filter((rule) => rule.statEffects).flatMap((rule) => rule.statEffects);
            statEffectRule = statEffectRules.find(
              (statEffectRule) => gameManager.figuresByIdentifier(statEffectRule.identifier, false, [monster]).length
            );
          }
        });
      }

      if (statEffectRule) {
        this.hasSpoiler = true;
        if (this.spoiler) {
          monster.statEffect = statEffectRule.statEffect;
          monster.statEffect.note = statEffectRule.note;
        }
      }
    });

    this.monsters = this.monsters
      .filter(
        (monsterData, index, self) =>
          !self.find((m) => monsterData.standeeShare === m.edition && monsterData.standeeShareEdition === m.name)
      )
      .sort((a, b) => {
        const textA = settingsManager.getLabel('data.monster.' + a.name).toLowerCase();
        const textB = settingsManager.getLabel('data.monster.' + b.name).toLowerCase();
        return textA < textB ? -1 : 1;
      });
  }

  openStats(monster: Monster) {
    const clone = new Monster(monster, monster.level);
    clone.statEffect = monster.statEffect;
    if (clone.boss) {
      gameManager.monsterManager.addMonsterEntity(clone, 1, MonsterType.boss);
    } else {
      gameManager.monsterManager.addMonsterEntity(clone, 1, MonsterType.elite);
      gameManager.monsterManager.addMonsterEntity(clone, 2, MonsterType.normal);
    }
    this.dialog.open(StatsListComponent, {
      panelClass: ['dialog'],
      data: { monster: clone, statEffectNote: clone.statEffect ? clone.statEffect.note : '' }
    });
  }
}
